<template>
  <modal v-if="state.isVisible"
         :custom-statuses="['exporting']"
         :prevent-esc="state.status === 'exporting'"
         :should-cancel-on-outside-click="state.status !== 'exporting'"
         :state="state"
         class="font-weight-normal"
         @toggle-modal="close"
  >
    <h3 class="modal-title">Export CSV</h3>

    <slot :slot="ModalStatus.Confirmation" :name="ModalStatus.Confirmation" />

    <div slot="exporting" class="text-left">
      <export-progress-title-block :title="config.title" />
      <p v-if="lastBroadcast.tries > 1" class="text-grey text-base-i mb-0-i underline">
        Attempt #{{lastBroadcast.tries}}
      </p>
      <p class="text-grey text-base-i mb-0-i">Time remaining: {{progressETAMessage}}</p>
      <p class="text-grey text-base-i mb-4-i">Completion: {{progressPercentMessage}}</p>
      <progress-bar :max="progress.max" :min="0" :value="progress.value" bar-classes="green" class="mb-5-i" />
      <p class="text-grey text-base-i mb-0-i">File is exporting. We will notify you when it's complete.</p>
    </div>

    <div :slot="ModalStatus.Successful" class="text-left">
      <export-progress-title-block :title="config.title" />
      <p class="text-grey text-base-i">Export is complete.</p>
      <template v-for="(message, status) in lastBroadcast.messages">
        <flash-attribute-messages :messages="message" :status="status" class="mb-5-i" />
      </template>
      <div class="cta">
        <a :href="downloadData.href" class="btn blue block" target="_blank" @click="downloaded">Download</a>
      </div>
    </div>

    <div :slot="ModalStatus.Failure" class="text-left">
      <export-progress-title-block :title="config.title" />
      <div class="em-block red p-5-i mb-5-i">
        <h5 class="mb-0-i text-base-i">Export failed:</h5>
        <p class="mb-0-i text-base-i">{{failMsg}}</p>
        <p class="mb-0-i text-base-i">{{failTimeDisplay}}</p>
      </div>
    </div>
  </modal>
</template>

<script lang="ts">
  import Vue from 'vue'
  import ProgressBar from '~/components/progress-bar.vue'
  import Progress from '~/helpers/progress'
  import ActionCableService from '~/services/actionCableService'
  import modalComponentMixin from '~/mixins/modalComponentMixin'
  import {ModalStatus} from '~/models/modalStatus'
  import ExportProgressTitleBlock from '~/components/export/export-progress-title-block.vue'
  import moment from 'moment'
  import DatetimeHelper from '~/helpers/datetimeHelper'
  import {ExportConfig, JobBroadcast} from '~/interfaces/exportInterfaces'
  import FlashAttributeMessages from '~/components/flash/flash-attribute-messages.vue'

  export default Vue.extend({
    inheritAttrs: false,
    components: {
      FlashAttributeMessages,
      ExportProgressTitleBlock,
      ProgressBar,
    },
    mixins: [
      modalComponentMixin,
    ],
    props: {
      config: {
        type: Object as () => ExportConfig,
      },
    },
    data(): Record<string, any> & { lastBroadcast: JobBroadcast } {
      return {
        actionCableService: new ActionCableService(this),
        progress: new Progress(),
        // @ts-ignore
        jobChannelName: this._uid,
        failMsg: '',
        failTimeDisplay: '',
        lastBroadcast: {
          progress_max: null,
          progress_value: null,
          is_final: null,
          job_id: null,
          mode: null,
          success: null,
          tries: 0,
        },
      }
    },
    channels: {
      computed: [{
        channelName() {
          return this.jobChannelName
        },
        connected() {
        },
        rejected() {
        },
        received(data: JobBroadcast) {
          this.lastBroadcast = data
          if ('error' in data || 'dead' in data) {
            this.fail(data)
            return
          }
          if (this.state.status === ModalStatus.Failure) {
            this.retry(data)
          }
          this.progress.update(data)
          if (data.is_final) {
            this.unsubscribe()
            this.progress.stop()
            if (data.success) {
              this.success()
            } else {
              this.fail({ ...data, retry: false, dead: true })
            }
          }
        },
        disconnected() {
        },
      }],
    },
    beforeDestroy() {
      this.unsubscribe()
    },
    computed: {
      progressPercentMessage(): string {
        return `${this.progress.formatPercent({ decimal: 2 })}% complete`
      },
      progressETAMessage(): string {
        if (this.progress.value && this.progress.isProgressing) {
          return `${this.progress.formatETA('HH:mm:ss')} hours (estimated)`
        } else {
          return '---'
        }
      },
      downloadData() {
        return this.lastBroadcast?.app_notification?.file_download_data?.[0] || {}
      },
    },
    methods: {
      subscribe(payload: { channel: string, id: string }) {
        this.actionCableService.subscribe(payload, this.jobChannelName)
        this.progress.startTime = Date.parse(this.config.started_at)
      },
      unsubscribe() {
        this.actionCableService.unsubscribe(this.jobChannelName)
      },
      downloadAvailable() {
        this.$emit('download-available', this.lastBroadcast)
      },
      downloaded(event) {
        this.downloadAvailable()
        this.$emit('downloaded')
        this.close(event)
      },
      exportDead(data) {
        this.$emit('export-dead', data)
      },
      close(event) {
        if (this.lastBroadcast.dead) this.exportDead(this.lastBroadcast)
        if (this.downloadData?.href) this.downloadAvailable()
        this.emitHideModal(event)
      },
      fail(data: { dead: boolean, retry: boolean, failed_at?: string }) {
        this.failTimeDisplay = null
        if (data.failed_at) this.failTimeDisplay = moment(Date.parse(data.failed_at)).format(DatetimeHelper.formats['datetime'])
        this.failMsg = 'Something went wrong.'
        if (!data.dead) this.failMsg += ' Export will retry automatically.'
        this.$emit('fail', data)
        this.progress.stop()
        if (!data.retry) this.unsubscribe()
      },
      success() {
        this.$emit('success')
      },
      retry(data: { progress_value: number, progress_max: number }) {
        this.progress.start(data)
        this.$emit('retry')
      },
    },
    watch: {
      'config.job_id': {
        handler(newVal, oldVal) {
          this.unsubscribe()
          if (newVal && this.state.status === 'exporting') {
            this.subscribe({ channel: this.config.channel_id, id: this.config.job_id })
          }
        },
        immediate: true,
      },
    },
  })
</script>
